import React, { useState } from 'react';

import Button from '@amzn/meridian/button';
import ControlGroup from '@amzn/meridian/control-group';
import { ModalFooter } from '@amzn/meridian/modal';
import RadioButton from '@amzn/meridian/radio-button';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import { APPROVED_TWP, DENIED_TWP, DLS_REFERRAL, INDEFINITE_RESTRICTIONS } from 'src/config/AccommodationStatusNames';
import PlacementDetailsService from 'src/services/placementDetailsService';
import {
  getPlacementDetails,
  setIsUpdateStatusModalOpen,
} from 'src/store/placementDetailsPageSlice/placementDetailsPageSlice';
import {
  type AccommodationStatus,
  type AccommodationStatusWithDecisionByLoginNeeded,
} from 'src/store/placementDetailsPageSlice/types';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { isCanadianSite } from 'src/utils/helpers';
import { filterIndefiniteRestrictionStatuses } from 'src/utils/placementDetailUtils';

import { UpdateStatusProgress } from '../constants';

interface StatusSelectionsProps {
  selectedStatus: AccommodationStatus | AccommodationStatusWithDecisionByLoginNeeded | undefined;
  setSelectedStatus: (val: AccommodationStatus | AccommodationStatusWithDecisionByLoginNeeded | undefined) => void;
  setProgress: (val: UpdateStatusProgress) => void;
}

// props isSiteParticipatingInDecisionHubPilot
const StatusSelection = ({ selectedStatus, setSelectedStatus, setProgress }: StatusSelectionsProps) => {
  const dispatch = useAppDispatch();
  const { featureFlags } = useAppSelector((state) => state.user);
  const {
    isSiteParticipatingInDecisionHubPilot,
    accommodationStatus,
    placementDetails: {
      accommodationType,
      accommodationStatus: placementCurrentStatus,
      associateFullName,
      login,
      countryId,
    },
    placementId,
  } = useAppSelector((state) => state.placementDetailsPage);
  const associateName: string = associateFullName ? associateFullName : login || '';

  const [showSpinner, setShowSpinner] = useState(false);

  const decisionHubEnabled = featureFlags.DECISION_PORTAL && isSiteParticipatingInDecisionHubPilot;

  const isDecisionMakerNeeded: boolean = decisionHubEnabled
    ? !!selectedStatus &&
      (accommodationType === 'DALI' || accommodationType === 'BALI') &&
      [APPROVED_TWP, DENIED_TWP, INDEFINITE_RESTRICTIONS].includes(selectedStatus.status)
    : !!selectedStatus && !!(selectedStatus as AccommodationStatusWithDecisionByLoginNeeded).decisionByLoginNeeded;

  const isDlsReferralInfoNeeded = !isCanadianSite(countryId) && selectedStatus?.status == DLS_REFERRAL;

  // Only show RTFD and Indefinite Restrictions if current status is Indefinite Restrictions.
  // Refer: https://issues.amazon.com/issues/P113097040
  const statuses =
    !decisionHubEnabled && placementCurrentStatus.status === INDEFINITE_RESTRICTIONS
      ? filterIndefiniteRestrictionStatuses(accommodationStatus)
      : accommodationStatus;

  const onSave = async () => {
    if (isDecisionMakerNeeded) {
      setProgress(UpdateStatusProgress.DecisionByLoginEntry);
    } else if (isDlsReferralInfoNeeded) {
      setProgress(UpdateStatusProgress.DlsReferralFormEntry);
    } else {
      setShowSpinner(true);
      try {
        const statusPayload = { accommodationId: placementId, accommodationStatusId: selectedStatus!.id };
        const updateStatusResponse = decisionHubEnabled
          ? await PlacementDetailsService.updateAccommodationStatusV2(statusPayload)
          : await PlacementDetailsService.updateAccommodationStatus(statusPayload);

        if (updateStatusResponse?.status === 200) {
          dispatch(getPlacementDetails(placementId));
          dispatch(setIsUpdateStatusModalOpen(false));
        }
      } finally {
        setShowSpinner(false);
      }
    }
  };

  return (
    <>
      <Text id="update-placement-modal-text">
        Update Placement Status for
        <b>{` ${associateName}`}</b>
      </Text>
      <Row widths="fill" spacingInset={'400 none'}>
        <LoadingSpinnerWrapper showSpinner={showSpinner}>
          <ControlGroup
            legend=""
            name="Placement Options"
            data-cy="placement-status__radio-btn-grp"
            onChange={(newStatus: string) => {
              setSelectedStatus(statuses?.find((val) => val.status == newStatus));
            }}
            value={selectedStatus?.status}
          >
            {statuses?.map((option, index) => (
              <RadioButton key={option.status} value={option.status} data-cy={`placement-status__${index}_radio-btn`}>
                {option.status}
              </RadioButton>
            ))}
          </ControlGroup>
        </LoadingSpinnerWrapper>
      </Row>
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fill">
          <Button
            id="update-placement-status-cancel-btn"
            data-cy="placement-status__cancel-btn"
            type="secondary"
            size="small"
            disabled={showSpinner}
            onClick={() => dispatch(setIsUpdateStatusModalOpen(false))}
          >
            Cancel
          </Button>
          <Button
            id="update-placement-status-save-btn-v2"
            data-cy="placement-status__save_radio-btn"
            type="primary"
            size="small"
            disabled={!selectedStatus || showSpinner}
            onClick={onSave}
          >
            {isDecisionMakerNeeded || isDlsReferralInfoNeeded ? 'Next' : 'Update Status'}
          </Button>
        </Row>
      </ModalFooter>
    </>
  );
};

export default StatusSelection;
